import React, { useState, useEffect, useRef } from 'react';
import {
  BonusLevelPayload,
  BonusGameType,
  BONUS_INTRO_MS,
  BONUS_COUNTDOWN_START_MS,
  BONUS_COUNTDOWN_STEP_MS,
} from '../../../constants/bonusConstants';
import { playBonusIntroSound, playBonusCountdownSound } from '../../../utils/sounds';
import { BonusIntro } from './BonusIntro';
import { BonusCountdown } from './BonusCountdown';
import { WhosOnImageHost } from './whosOnImage/WhosOnImageHost';
import { FastTypingHost } from './fastTyping/FastTypingHost';
import { FastFingersHost } from './fastFingers/FastFingersHost';
import { CatchTheTrainHost } from './catchTheTrain/CatchTheTrainHost';
import { QuickMathHost } from './quickMath/QuickMathHost';
import { SaveTheAnimalsHost } from './saveTheAnimals/SaveTheAnimalsHost';
import { PuzzleHost } from './puzzle/PuzzleHost';
import { FlipCard } from '../../shared/FlipCard/FlipCard';
import { useTranslation } from '../../../i18n';
import { getBonusIntro } from '../../../i18n/helpers';
import '../../../styles/bonus-intro.css';
import '../../../styles/bonus-countdown.css';

export type BonusHostPhase = 'intro' | 'playing' | 'results';

interface BonusLevelHostProps {
  payload: BonusLevelPayload | null;
  phase: BonusHostPhase;
  onPhaseChange?: (phase: BonusHostPhase) => void;
  /** When true, fast-typing host stops typewriter and sound (e.g. everyone submitted). */
  allAnswered?: boolean;
  /** When true, round ended (everyone answered or time up). Show flip to answer + loading next question. */
  roundEnded?: boolean;
}

/**
 * Left-column content only for host during bonus.
 * Renders: intro (title + description) -> game-specific view. When round ends (everyone answered or time up),
 * same screen shows flip to correct answer (where applicable) + "Loading next question..." slider; no separate outro screen.
 * Parent supplies layout (timer + this + StandingsSidebar).
 */
export const BonusLevelHost: React.FC<BonusLevelHostProps> = ({
  payload,
  phase,
  onPhaseChange,
  allAnswered = false,
  roundEnded = false,
}) => {
  const { t } = useTranslation();
  const [internalPhase, setInternalPhase] = useState<BonusHostPhase>(phase);

  useEffect(() => {
    setInternalPhase(phase);
  }, [phase]);

  const currentPhase = phase ?? internalPhase;
  const gameType = (payload?.bonusGameType ?? 'puzzle') as BonusGameType;
  const introSoundPlayedRef = useRef<string | null>(null);
  /** Keep puzzle imageUrl once set so it persists when room-state overwrites payload (server doesn't store imageUrl). */
  const puzzleImageUrlRef = useRef<{ questionId: string; url: string } | null>(null);
  /** When intro phase started (for 3-2-1 countdown timing). */
  const introStartTimeRef = useRef<number>(0);
  /** QuestionId we've started intro for; prevents resetting countdown on focus/room-state refresh. */
  const introStartedForQuestionRef = useRef<string | null>(null);
  const [countdownDigit, setCountdownDigit] = useState<3 | 2 | 1 | null>(null);

  // Play intro sound once when entering intro for this bonus round
  useEffect(() => {
    if (currentPhase !== 'intro' || !payload?.questionId) return;
    if (introSoundPlayedRef.current === payload.questionId) return;
    introSoundPlayedRef.current = payload.questionId;
    playBonusIntroSound();
  }, [currentPhase, payload?.questionId]);

  // Preload whos-on-image images during intro so they're cached when playing starts
  useEffect(() => {
    if (currentPhase !== 'intro' || payload?.bonusGameType !== 'whos-on-image') return;
    const primary = payload.imageUrl;
    const fallback = payload.fallbackImageUrl;
    if (primary) {
      const img = new Image();
      img.src = primary;
    }
    if (fallback) {
      const img2 = new Image();
      img2.src = fallback;
    }
  }, [currentPhase, payload?.bonusGameType, payload?.imageUrl, payload?.fallbackImageUrl]);

  // Preload puzzle image during intro (same as players: image ready when playing starts)
  useEffect(() => {
    if (currentPhase !== 'intro' || payload?.bonusGameType !== 'puzzle' || !payload?.imageUrl) return;
    const img = new Image();
    img.src = payload.imageUrl;
  }, [currentPhase, payload?.bonusGameType, payload?.imageUrl]);

  // Track intro start for countdown; reset countdown when leaving intro.
  // Only set introStartTimeRef when entering intro for a NEW question - not when payload ref
  // changes (e.g. room-state refresh on tab focus), which would restart the countdown and replay sound.
  // Deps use questionId only: quick-math (and similar) rebroadcast room-state on every score tick with a new payload object.
  useEffect(() => {
    const qid = payload?.questionId;
    if (currentPhase === 'intro' && qid) {
      if (introStartedForQuestionRef.current !== qid) {
        introStartedForQuestionRef.current = qid;
        introStartTimeRef.current = Date.now();
      }
    } else {
      setCountdownDigit(null);
      if (currentPhase !== 'intro') {
        introStartedForQuestionRef.current = null;
      }
    }
  }, [currentPhase, payload?.questionId]);

  // 3-2-1 countdown: from BONUS_COUNTDOWN_START_MS show 3, then 2, then 1 (each BONUS_COUNTDOWN_STEP_MS)
  useEffect(() => {
    if (currentPhase !== 'intro' || !payload?.questionId) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - introStartTimeRef.current;
      if (elapsed < BONUS_COUNTDOWN_START_MS) {
        setCountdownDigit(null);
        return;
      }
      if (elapsed >= BONUS_INTRO_MS) {
        setCountdownDigit(null);
        return;
      }
      const step = elapsed - BONUS_COUNTDOWN_START_MS;
      if (step < BONUS_COUNTDOWN_STEP_MS) setCountdownDigit(3);
      else if (step < 2 * BONUS_COUNTDOWN_STEP_MS) setCountdownDigit(2);
      else setCountdownDigit(1);
    }, 100);
    return () => clearInterval(interval);
  }, [currentPhase, payload?.questionId]);

  // Play countdown sound once at start of countdown (when 3 appears); sound file beeps 3 times for 3-2-1
  const countdownSoundPlayedRef = useRef(false);
  useEffect(() => {
    if (countdownDigit === 3 && !countdownSoundPlayedRef.current) {
      countdownSoundPlayedRef.current = true;
      playBonusCountdownSound();
    }
    if (countdownDigit == null) countdownSoundPlayedRef.current = false;
  }, [countdownDigit]);

  // Auto-advance from intro to playing after BONUS_INTRO_MS (can be driven by parent).
  // Must not depend on full `payload`: quick-math calls broadcastRoomState on every correct step → new object every tick → timeout never fires if we reset on each.
  useEffect(() => {
    if (currentPhase !== 'intro' || !payload?.questionId) return;
    const introTimer = setTimeout(() => {
      setInternalPhase('playing');
      onPhaseChange?.('playing');
    }, BONUS_INTRO_MS);
    return () => clearTimeout(introTimer);
  }, [currentPhase, payload?.questionId, onPhaseChange]);

  if (!payload) {
    return <div className="bonus-host-glow-text--muted" style={{ padding: '1rem' }}>{t('bonus.host.loadingBonus')}</div>;
  }

  if (currentPhase === 'intro') {
    const isLastRound = payload.isLastBonusRound ?? (payload.questionNumber != null && payload.totalQuestions != null && payload.questionNumber >= payload.totalQuestions);
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <BonusIntro bonusGameType={gameType} isLastRound={isLastRound} payload={payload} />
        {countdownDigit != null && <BonusCountdown digit={countdownDigit} />}
      </div>
    );
  }

  // No separate results/outro screen: stay on same screen, show flip + loading next question when round ended

  // Phase === 'playing' (or 'results'): game title on host, then game-specific content. When roundEnded, same screen + flip + loading bar.
  const playingTitle =
    gameType === 'catch-me-if-you-can' && payload?.themeTitle
      ? payload.themeTitle
      : getBonusIntro(gameType).title;
  const titleBlock = playingTitle ? (
    <h2 className="bonus-game-playing-title" style={{ marginBottom: '1rem', color: 'var(--color-primary)' }}>
      {playingTitle}
    </h2>
  ) : null;

  // No "Bonus Level Finished" on host — it's already visible on the player screen.

  // For whos-on-image: flip the title to show correct answer when round ends.
  const whosOnImageTitleFlip = (
    <div style={{ flexShrink: 0, margin: '0 0 0.35rem', width: '100%', textAlign: 'center', minHeight: '5rem' }}>
      <FlipCard
        flipped={roundEnded && !!payload.correctAnswer}
        isFadingOut={false}
        front={
          <h2 className="bonus-game-playing-title" style={{ margin: 0, color: 'var(--color-primary)' }}>
            {playingTitle}
          </h2>
        }
        back={
          <div style={{ textAlign: 'center' }}>
            <div className="bonus-host-glow-text--muted" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>{t('bonus.correctAnswerFlip')}</div>
            <h2 className="bonus-host-glow-text" style={{ margin: 0, fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', fontWeight: 'bold', letterSpacing: '0.04em' }}>
              {payload.correctAnswer}
            </h2>
          </div>
        }
      />
    </div>
  );

  // Persist puzzle imageUrl so it survives room-state updates (server doesn't include it)
  if (payload.bonusGameType === 'puzzle' && payload.questionId) {
    if (puzzleImageUrlRef.current?.questionId !== payload.questionId) {
      puzzleImageUrlRef.current = payload.imageUrl
        ? { questionId: payload.questionId, url: payload.imageUrl }
        : null;
    } else if (payload.imageUrl) {
      puzzleImageUrlRef.current = { ...puzzleImageUrlRef.current!, url: payload.imageUrl };
    }
  }
  const displayPuzzleImageUrl =
    payload.bonusGameType === 'puzzle'
      ? (payload.imageUrl ||
        (puzzleImageUrlRef.current?.questionId === payload?.questionId ? puzzleImageUrlRef.current?.url : null))
      : null;

  switch (payload.bonusGameType) {
    case 'puzzle':
      return <PuzzleHost imageUrl={displayPuzzleImageUrl ?? ''} />;
    case 'word-order': {
      const wordOrderAnswer = payload.correctAnswer ?? '';
      const wordLen = Math.max(wordOrderAnswer.length, 1);
      /** Shrink fluid vw for long words so the full word fits on narrow / mobile viewports */
      const wordOrderVw = Math.min(14, (wordLen >= 7 ? 80 : 88) / wordLen);
      return (
        <div style={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
          {titleBlock}
          <div
            style={{
              padding: '2rem max(1rem, env(safe-area-inset-right)) 2rem max(1rem, env(safe-area-inset-left))',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <span
              className="bonus-word-order-answer"
              style={{
                fontSize: `clamp(1.35rem, ${wordOrderVw}vw, 6rem)`,
                fontWeight: 'bold',
                letterSpacing:
                  wordLen >= 9 ? '0.06em' : wordLen >= 7 ? '0.08em' : wordLen > 12 ? '0.06em' : '0.12em',
                display: 'inline-block',
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {wordOrderAnswer}
            </span>
          </div>
        </div>
      );
    }
    case 'whos-on-image':
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', minHeight: 0 }}>
          {whosOnImageTitleFlip}
          <WhosOnImageHost
            imageUrl={payload.imageUrl ?? ''}
            fallbackImageUrl={payload.fallbackImageUrl}
            timeLimit={payload.timeLimit ?? 20000}
            allAnswered={allAnswered}
            roundEnded={roundEnded}
            correctAnswer={payload.correctAnswer ?? ''}
          />
        </div>
      );
    case 'fast-typing':
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', minHeight: 0 }}>
          <h2 className="bonus-game-playing-title" style={{ margin: '0 0 0.35rem', color: 'var(--color-primary)', flexShrink: 0 }}>
            {playingTitle}
          </h2>
          <FastTypingHost
            text={payload.text ?? ''}
            timeLimit={payload.timeLimit ?? 30000}
            roundEnded={roundEnded}
          />
        </div>
      );
    case 'catch-me-if-you-can':
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', minHeight: 0 }}>
          {currentPhase !== 'results' && (
            <h2 className="bonus-game-playing-title" style={{ margin: '0 0 0.35rem', color: 'var(--color-primary)', flexShrink: 0 }}>
              {payload.themeTitle ?? playingTitle}
            </h2>
          )}
          <SaveTheAnimalsHost
            themeTitle={payload.themeTitle ?? getBonusIntro('catch-me-if-you-can').title}
            animals={payload.animals ?? ['🐱', '🐕', '🐦']}
            roundEnded={roundEnded}
          />
        </div>
      );
    case 'fast-fingers':
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', minHeight: 0 }}>
          {currentPhase !== 'results' && (
            <h2 className="bonus-game-playing-title" style={{ margin: '0 0 0.35rem', color: 'var(--color-primary)', flexShrink: 0 }}>
              {playingTitle}
            </h2>
          )}
          <FastFingersHost
            successEmoji={payload.successEmoji ?? '⚡'}
            wrongEmojis={payload.wrongEmojis ?? ['❌']}
            successPoints={payload.successPoints ?? 10}
            wrongPenalty={payload.wrongPenalty ?? 5}
            timeLimit={payload.timeLimit ?? 30000}
            roundEnded={roundEnded}
          />
        </div>
      );
    case 'catch-the-train':
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', minHeight: 0 }}>
          {currentPhase !== 'results' && (
            <h2 className="bonus-game-playing-title" style={{ margin: '0 0 0.35rem', color: 'var(--color-primary)', flexShrink: 0 }}>
              {payload.themeTitle ?? playingTitle}
            </h2>
          )}
          <CatchTheTrainHost
            themeTitle={payload.themeTitle ?? getBonusIntro('catch-the-train').title}
            emoji={payload.emoji ?? '🚂'}
            roundEnded={roundEnded}
          />
        </div>
      );
    case 'quick-math':
      return <QuickMathHost payload={payload} roundEnded={roundEnded} />;
    default:
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
          {titleBlock}
          <div>{t('bonus.unknownGame', { type: payload.bonusGameType })}</div>
        </div>
      );
  }
};
